import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Heading,
  Stack,
  Text,
  TextLink,
  useResponsiveValue
} from '@machobear/design-system'

import emailIcon from '@/features/Company/icons/email-icon.png'
import globeIcon from '@/features/Company/icons/globe-icon.png'
import phoneIcon from '@/features/Company/icons/phone-icon.png'
import Head from '@/components/head'

import titleLogo from './logo.png'

type Props = {
  metadata?: {
    __typename: string
    page: number
    page_size: number
    total_count: number
  }
  footerOnScreen: boolean
  listingsOnScreen: boolean
}

const CLVGroupProfile = ({ metadata }: Props) => {
  const { t } = useTranslation('clvgroup')
  const profile = {
    title: 'CLV Group',
    url: '/company/clvgroup'
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const isDesktop = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true
  })

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <Box
        style={{ height: isMobile ? '210px' : isDesktop ? '575px' : '350px' }}
        marginBottom="gutter"
        overflow="hidden"
        position="relative"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="full"
          width="full"
          overflow="hidden"
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            src={
              isMobile
                ? '/videos/clvgroup-low-res.mp4'
                : '/videos/clvgroup-high-res.mp4'
            }
          />
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        width="full"
        marginBottom="large"
      >
        <Stack space="medium">
          <Box display="flex" alignItems="stretch" marginRight="large">
            <Box
              marginRight="xsmall"
              marginLeft="medium"
              paddingTop="small"
              style={{ flexShrink: 0 }}
            >
              <Image
                src={titleLogo.src}
                width={110}
                height={32}
                alt="CLV Group"
              />
            </Box>
            <Heading level="3" weight="weak">
              CLV Group
            </Heading>
          </Box>
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            gap={{ mobile: 'gutter', tablet: 'none' }}
            marginX="medium"
            marginBottom="xlarge"
            marginTop="medium"
            style={{
              maxWidth: '1096px'
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              marginRight="xxlarge"
              style={{
                width: isMobile ? '100%' : 'fit-content'
              }}
            >
              <Stack space="medium">
                <Stack space="gutter">
                  <Text size="standard" weight="strong">
                    {t('clvgroup:description')}
                  </Text>
                </Stack>
              </Stack>
            </Box>
            <Box
              background="neutralSoft"
              borderRadius="small"
              paddingY="small"
              paddingX="gutter"
              marginTop={{ mobile: 'gutter', tablet: 'none' }}
              style={{
                height: 'min-content',
                width: isMobile ? '100%' : 'fit-content',
                minWidth: '300px'
              }}
            >
              <Stack space="medium">
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={phoneIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink href="tel:1-613-728-2000" rel="noreferrer noopener">
                    1-613-728-2000
                  </TextLink>
                </Box>

                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={globeIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href="https://www.clvgroup.com/apartment-rentals/"
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    www.clvgroup.com
                  </TextLink>
                </Box>

                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={emailIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href="mailto:info@clvgroup.com"
                    rel="noreferrer noopener"
                  >
                    info@clvgroup.com
                  </TextLink>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>
      <Box textAlign="center" paddingBottom="gutter">
        <Heading level="6">{t('clvgroup:Our Portfolio')}</Heading>
      </Box>
    </>
  )
}

export default CLVGroupProfile
